import type { INameTag } from '../IRenderer';
import { DisplayPool } from '../pool/DisplayPool';
import { SpriteLabel } from './SpriteLabel';
import type { FloatingLabelStyle } from '../utils/FloatingLabel';

/**
 * Pooled SpriteLabels for floating combat text (plan 16 §2.3). A damage
 * number used to be a new `Text` (an addChild, a zIndex write and a
 * removeChild — three stage rebuilds per number). A pooled label stays
 * attached at its depth, parked at alpha 0, and takes its next number by
 * swapping atlas frames.
 *
 * FCT keeps the old `PixiNameTag` defaults: Arial, normal weight, 2 px
 * black stroke, centred.
 */
const FCT_FONT = { fontFamily: 'Arial, sans-serif', fontWeight: 'normal' as const, strokeWidth: 2 };

const pool = new DisplayPool<SpriteLabel>({ perKeyCap: 48, globalCap: 96 });
const keys = new WeakMap<INameTag, string>();

export function acquirePooledFloatingLabel(x: number, y: number, text: string, style: FloatingLabelStyle): INameTag {
  const key = `${style.fontSize}|${style.depth}`;
  let label = pool.take(key);
  if (label) {
    label.setText(text);
    label.setColor(style.color);
    label.setPosition(x, y);
  } else {
    pool.noteMade();
    label = new SpriteLabel(x, y, text, {
      ...FCT_FONT,
      fontSize: style.fontSize,
      color: style.color,
      depth: style.depth,
      anchorX: 0.5,
      anchorY: 0.5,
    });
    keys.set(label, key);
  }
  return label;
}

export function releasePooledFloatingLabel(label: INameTag): void {
  const key = keys.get(label);
  if (key === undefined || !(label instanceof SpriteLabel)) {
    label.destroy();
    return;
  }
  pool.release(key, label);
}

/** Pool counters for diagnostics + tests. */
export function getFloatingLabelPoolStats() {
  return pool.stats();
}
